import type { MctProvider, SearchCenter } from "../@types";

export function getProviderNetworkFailure(headers?: Headers) {
  return (
    !!headers &&
    headers
      .entries()
      .some(
        ([key, value]) =>
          !!(key === "failed-integrations" && value === "provider-network")
      )
  );
}

/**
 * toCoverageEntry formats one entry for the plan endpoints' providers and
 * facilities parameters. An entry is a bare NPI, or "NPI:latitude:longitude"
 * when a center of search is available. The backend evaluates each NPI from
 * its inline center and falls back to the request-level latitude/longitude.
 * Non-finite coordinates produce a bare entry because the backend rejects
 * them with a 400.
 */
export function toCoverageEntry(
  npi: string,
  searchCenter?: SearchCenter
): string {
  if (
    !searchCenter ||
    !Number.isFinite(searchCenter.latitude) ||
    !Number.isFinite(searchCenter.longitude)
  ) {
    return npi;
  }
  return `${npi}:${searchCenter.latitude}:${searchCenter.longitude}`;
}

/**
 * getProviderCoverageEntries formats the providers parameter entries for the
 * stored providers. Entries are sorted so the stored order cannot produce
 * distinct query keys for the same providers. Pass includeSearchCenters as
 * false outside the MAPNET gate: stored centers persist after the flag turns
 * off, and bare entries keep those requests on the legacy wire format that
 * every deployed API version accepts.
 */
export function getProviderCoverageEntries(
  providers: MctProvider[],
  includeSearchCenters: boolean
): string[] {
  return providers
    .map(p =>
      toCoverageEntry(p.npi, includeSearchCenters ? p.searchCenter : undefined)
    )
    .sort((a, b) => a.localeCompare(b));
}

/**
 * normalizeCoverageEntries returns a sorted copy of the entries, or
 * undefined when the list is empty or missing. Entry order and [] versus
 * undefined then cannot produce distinct query keys, so identical requests
 * share one cache entry.
 */
export function normalizeCoverageEntries(
  entries?: string[]
): string[] | undefined {
  if (!entries?.length) {
    return undefined;
  }
  return [...entries].sort((a, b) => a.localeCompare(b));
}
